import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import styled from 'styled-components';
import FunnelShell from './funnel/FunnelShell';
import {
  PRICEBOOK_SERVICES,
  type PriceBookSku,
  type ServiceDefinition,
} from '../lib/pricebook/pricebook';
import { formatUsdCents } from '../lib/pricing/formatMoney';
import { usePricebookCatalogSync } from '../hooks/usePricebookCatalogSync';

const Header = styled.header`
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const PrimaryLink = styled(Link)`
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-weight: 700;
`;

const GhostLink = styled(Link)`
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-weight: 700;
`;

const Section = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.gray050};
  padding: 1rem;
  display: grid;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const TierGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const TierCard = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 12px;
  padding: 0.8rem;
  display: grid;
  gap: 0.35rem;
`;

function formatSkuPrice(sku: PriceBookSku): string {
  const amount = formatUsdCents(sku.amountCents);
  return sku.billingType === 'recurring' ? `${amount}/mo` : `${amount} one-time`;
}

function ServicePricingSection({ service }: { service: ServiceDefinition }) {
  return (
    <Section>
      <h2 style={{ margin: 0 }}>{service.title}</h2>
      <p style={{ margin: 0 }}>{service.subtitle}</p>
      <TierGrid>
        {service.tiers.map((tier) => (
          <TierCard key={tier.skuId}>
            <strong>{tier.name}</strong>
            <span>{formatSkuPrice(tier)}</span>
            <span>{tier.description}</span>
          </TierCard>
        ))}
      </TierGrid>
      {service.addOns.length ? (
        <>
          <h3 style={{ margin: 0 }}>Required one-time onboarding</h3>
          <TierGrid>
            {service.addOns.map((addOn) => (
              <TierCard key={addOn.skuId}>
                <strong>{addOn.name}</strong>
                <span>{formatSkuPrice(addOn)}</span>
                <span>{addOn.description}</span>
              </TierCard>
            ))}
          </TierGrid>
        </>
      ) : null}
      {service.checkoutEnabled ? (
        <div>
          <PrimaryLink to={`/services/${service.slug}`}>
            Select {service.title}
          </PrimaryLink>
        </div>
      ) : (
        <div>
          <a href="mailto:hello@cybard.co?subject=CyBard%20Custom%20Project%20Quote">
            Request a quote
          </a>
        </div>
      )}
    </Section>
  );
}

export default function PricingPage() {
  const pricebookVersion = usePricebookCatalogSync();
  const services = useMemo(
    () => {
      void pricebookVersion;
      return [
        PRICEBOOK_SERVICES.platform,
        PRICEBOOK_SERVICES.marketing,
        PRICEBOOK_SERVICES.assets_design,
        PRICEBOOK_SERVICES.custom,
      ];
    },
    [pricebookVersion],
  );

  return (
    <FunnelShell>
      <Header>
        <h1 style={{ margin: 0 }}>Pricing</h1>
        <p style={{ margin: 0 }}>
          All prices are shown in USD. Final totals are calculated server-side at
          checkout for integrity.
        </p>
      </Header>
      <Actions>
        <PrimaryLink to="/services">Build your package</PrimaryLink>
        <GhostLink to="/checkout">Go to checkout</GhostLink>
      </Actions>
      {services.map((service) => (
        <ServicePricingSection key={service.slug} service={service} />
      ))}
    </FunnelShell>
  );
}
